import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';

// Dashboard reads own presentation only, not authentication or transport retry.
export default function useDashboardRead(queryKey, load) {
  const revision = useSyncExternalStore(
    subscribeActiveSessionRevision, getActiveSessionRevision, getActiveSessionRevision,
  );
  const owner = useMemo(() => ({ revision, queryKey, active: false, pending: false }), [revision, queryKey]);
  const current = useRef(owner);
  current.current = owner;
  const [result, setResult] = useState(null);
  const owns = useCallback(() => owner.active && current.current === owner
    && owner.revision === getActiveSessionRevision(), [owner]);
  const refresh = useCallback(async () => {
    if (!owns() || owner.pending) return;
    owner.pending = true;
    const attempt = {};
    owner.attempt = attempt;
    const ownsAttempt = () => owns() && owner.attempt === attempt;
    setResult({ owner, status: 'loading' });
    try {
      const data = await load(ownsAttempt);
      if (ownsAttempt()) setResult({ owner, status: 'success', data });
    } catch {
      if (ownsAttempt()) setResult({ owner, status: 'unavailable' });
    } finally {
      if (owner.attempt === attempt) owner.pending = false;
    }
  }, [load, owner, owns]);
  useEffect(() => {
    owner.active = true;
    refresh();
    return () => { owner.active = false; owner.pending = false; owner.attempt = null; };
  }, [owner, refresh]);
  const visible = result?.owner === owner && owner.revision === getActiveSessionRevision()
    ? result : { status: 'loading' };
  return { ...visible, refresh };
}
